import React from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Sparkles, ArrowRight, Leaf, Droplets } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useConfig } from "../lib/useConfig";
import TalkWithSpecialist from "../components/TalkWithSpecialist";

export default function Home() {
  const navigate = useNavigate();
  const { clear } = useSession();
  const cfg = useConfig();

  const start = (pathway) => {
    clear();
    navigate(`/intake?pathway=${pathway}`);
  };

  return (
    <div className="relative min-h-[100dvh] gf-gradient-dark gf-grain overflow-hidden">
      <div className="relative mx-auto max-w-3xl px-5 pt-8 pb-16 gf-fade-in">
        {/* Logo band — full width with padding */}
        <div
          data-testid="home-logo-container"
          className="relative rounded-3xl overflow-hidden bg-white shadow-2xl shadow-emerald-950/40 border border-white/40"
        >
          <button
            type="button"
            data-testid="banner-diagnose-hotspot"
            aria-label="Diagnose My Landscape"
            onClick={() => start("diagnose")}
            className="absolute z-10 rounded-[3%] hover:ring-4 hover:ring-sky-400/60 transition-shadow"
            style={{ left: "6.2%", top: "59%", width: "43.1%", height: "20.3%" }}
          />
          <button
            type="button"
            data-testid="banner-improve-hotspot"
            aria-label="Improve My Landscape"
            onClick={() => start("improve")}
            className="absolute z-10 rounded-[3%] hover:ring-4 hover:ring-emerald-400/60 transition-shadow"
            style={{ left: "50.6%", top: "59%", width: "43.2%", height: "20.3%" }}
          />
          <img
            data-testid="home-logo-image"
            src="/greenflow-hero-banner.webp"
            alt="GreenFlow Landscape Vision — Powered by Ask Dr. Verdant"
            className="block w-full h-auto select-none"
            draggable={false}
          />
        </div>

        {/* Tagline strip */}
        <div className="mt-6 text-center">
          <h1
            data-testid="home-tagline"
            className="mt-4 font-display text-white text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight"
          >
            See the Possibilities.
            <br />
            <span className="text-emerald-300">Improve the Landscape.</span>
          </h1>
          <p className="mt-3 text-emerald-100/80 text-sm sm:text-base max-w-md mx-auto">
            Snap your yard. Choose a path. Let a GreenFlow specialist show you
            what&apos;s wrong — or what it could become.
          </p>
        </div>

        {/* Pathways */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <button
            data-testid="pathway-diagnose-button"
            onClick={() => start("diagnose")}
            className="group relative text-left rounded-3xl p-6 bg-white hover:bg-sky-50 transition border border-white/40 gf-shadow-glow"
          >
            <div className="flex items-start justify-between">
              <div className="h-12 w-12 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center">
                <Stethoscope size={24} />
              </div>
              <ArrowRight
                size={22}
                className="text-sky-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition"
              />
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-widest font-bold text-sky-700">
                Pathway 01
              </div>
              <div className="font-display text-xl font-bold text-[#092C15] mt-1">
                Diagnose My Landscape
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Something doesn&apos;t look right.
              </div>
            </div>
          </button>

          <button
            data-testid="pathway-improve-button"
            onClick={() => start("improve")}
            className="group relative text-left rounded-3xl p-6 bg-white hover:bg-emerald-50 transition border border-white/40 gf-shadow-glow"
          >
            <div className="flex items-start justify-between">
              <div className="h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
                <Sparkles size={24} />
              </div>
              <ArrowRight
                size={22}
                className="text-emerald-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition"
              />
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                Pathway 02
              </div>
              <div className="font-display text-xl font-bold text-[#092C15] mt-1">
                Improve My Landscape
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Show me what this property could become.
              </div>
            </div>
          </button>
          {cfg?.verdant_enabled && <TalkWithSpecialist />}
        </div>

        {/* Capability pills */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 text-emerald-100/80">
          {[
            { label: "Plant Health", icon: Leaf },
            { label: "Irrigation", icon: Droplets },
            { label: "Turf Care", icon: Sparkles },
          ].map(({ label, icon: Icon }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs"
            >
              <Icon size={12} /> {label}
            </span>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-emerald-100/50">
          A product of GreenFlow Landcare &amp; Irrigation, LLC
        </p>
      </div>
    </div>
  );
}
